import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '~/components/common/search-input';
import { Separator } from '~/components/ui/separator';
import { cn } from '~/utils/cn';

/** Row list beyond this count reveals a title search box. */
const SEARCH_THRESHOLD = 5;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AttributeComparisonItem {
  /** Row label. Pre-translated by the caller. */
  title: string;
  /** Value before enrichment. Interpreted relative to `maxValue`. */
  beforeValue: number;
  /** Value after enrichment. Interpreted relative to `maxValue`. */
  afterValue: number;
  /** Optional short label rendered as a thin pill next to the title (e.g. trend kind). */
  badge?: string;
  /** Optional trend classification carried through from the API for badging. */
  trendKind?: 'micro' | 'macro';
  /** Raw product counts behind this row's before/after values, when the
   *  backend sends them (currently only OCCASIONS_COVERAGE — trend rows'
   *  `beforeValue`/`afterValue` already *are* raw counts). Not rendered by
   *  this list itself; a `getRowClickHandler` reads these to build the
   *  drill-down drawer's header stats without a second data source. */
  countBefore?: number;
  countAfter?: number;
}

interface AttributeComparisonListProps {
  /** Section heading. Pre-translated by the caller. */
  title: string;
  /** Section sub-heading. Pre-translated by the caller. */
  subtitle: string;
  /** Ordered list of items to display. */
  items: AttributeComparisonItem[];
  /** Upper bound used to normalise bar widths (e.g. 100 for %, dataset max for counts). */
  maxValue: number;
  /** Formats the displayed value next to each bar (e.g. v => `${v}%` or String(v)). */
  formatValue: (v: number) => string;
  /** When true, hides the Before/After colour legend in the header. Default false. */
  hideLegend?: boolean;
  /** When false, hides the Before bar in every row — used by a shared legend
   *  filter that solos the After series. Defaults to shown. */
  showBefore?: boolean;
  /** When false, hides the After bar in every row. Defaults to shown. */
  showAfter?: boolean;
  /** Called per row to get a click handler — return `undefined` to leave
   *  that specific row non-interactive (e.g. a Top Trends row with no
   *  determinable membership category to query with). A row only gains
   *  cursor-pointer, hover highlight, and keyboard support when this
   *  returns a real handler for it. */
  getRowClickHandler?: (item: AttributeComparisonItem) => (() => void) | undefined;
  /** `data-testid` root; derives `-title`, `-search`, `-empty`, `-row-{title-slug}` (+ `-before-value`, `-after-value`). */
  testId?: string;
}

/** Kebab-cases a row title so it can key a `data-testid`. */
function toTestIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Coloured circle + label legend pill. */
function LegendItem({ color, label }: { color: 'before' | 'after'; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={cn(
          'h-3 w-3 shrink-0 rounded-full',
          color === 'before' ? 'bg-warning' : 'bg-primary',
        )}
      />
      <span className="text-text-date-pill text-sm font-medium">{label}</span>
    </div>
  );
}

/**
 * Single before/after bar row for raw values (percentages, counts, etc.).
 * Bar widths are proportional to `maxValue`; value is formatted by `formatValue`.
 */
function ComparisonRow({
  title,
  beforeValue,
  afterValue,
  maxValue,
  formatValue,
  badge,
  showBefore = true,
  showAfter = true,
  onRowClick,
  testId,
}: {
  testId: string;
  title: string;
  beforeValue: number;
  afterValue: number;
  maxValue: number;
  formatValue: (v: number) => string;
  badge?: string;
  showBefore?: boolean;
  showAfter?: boolean;
  onRowClick?: () => void;
}) {
  const beforePct = maxValue > 0 ? (beforeValue / maxValue) * 100 : 0;
  const afterPct = maxValue > 0 ? (afterValue / maxValue) * 100 : 0;

  return (
    <div
      data-testid={testId}
      className={cn(
        'group flex items-center gap-3 py-2',
        onRowClick && 'hover:bg-muted/50 -mx-2 cursor-pointer rounded-md px-2 transition-colors',
      )}
      {...(onRowClick && {
        role: 'button',
        tabIndex: 0,
        onClick: onRowClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick();
          }
        },
      })}
    >
      <span className="text-foreground flex w-36 shrink-0 flex-col items-start gap-1 text-sm font-normal">
        <span className="inline-flex items-center gap-1.5">
          {title}
          {onRowClick && (
            <ChevronRight
              className="text-muted-foreground h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />
          )}
        </span>
        {badge && (
          <span className="border-border text-muted-foreground rounded-full border px-1.5 text-[10px] leading-4 font-medium">
            {badge}
          </span>
        )}
      </span>
      <div className="flex flex-1 flex-col gap-1.5">
        {showBefore && (
          <div className="flex items-center gap-2">
            <div className="bg-surface-date-pill h-[7px] flex-1 overflow-hidden rounded-full">
              <div
                className="bg-warning h-full rounded-full transition-[width] duration-500"
                style={{ width: `${String(beforePct)}%` }}
              />
            </div>
            <span
              data-testid={`${testId}-before-value`}
              className="text-warning w-8 text-right text-xs font-medium tabular-nums"
            >
              {formatValue(beforeValue)}
            </span>
          </div>
        )}
        {showAfter && (
          <div className="flex items-center gap-2">
            <div className="bg-surface-date-pill h-[7px] flex-1 overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-[width] duration-500"
                style={{ width: `${String(afterPct)}%` }}
              />
            </div>
            <span
              data-testid={`${testId}-after-value`}
              className="text-primary w-8 text-right text-xs font-medium tabular-nums"
            >
              {formatValue(afterValue)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Titled attribute list that renders before/after bar rows for raw values
 * (percentages, counts) rather than the 0–10 score scale used by
 * AttributeScoreList. No delta chip is shown.
 */
export function AttributeComparisonList({
  title,
  subtitle,
  items,
  maxValue,
  formatValue,
  hideLegend = false,
  showBefore = true,
  showAfter = true,
  getRowClickHandler,
  testId = 'attribute-comparison-list',
}: AttributeComparisonListProps) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');

  const showSearch = items.length > SEARCH_THRESHOLD;
  const filteredItems = useMemo(() => {
    if (!showSearch || !search.trim()) return items;
    const query = search.trim().toLowerCase();
    return items.filter((item) => item.title.toLowerCase().includes(query));
  }, [items, search, showSearch]);

  return (
    <div data-testid={testId} className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col gap-1">
          <p data-testid={`${testId}-title`} className="text-foreground text-base font-medium">
            {title}
          </p>
          <p className="text-muted-foreground text-xs font-light">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {!hideLegend && (
            <>
              <LegendItem color="before" label={t('scoreComparisonBar.beforeLabel')} />
              <LegendItem color="after" label={t('scoreComparisonBar.afterLabel')} />
            </>
          )}
          {showSearch && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('attributeComparisonList.searchPlaceholder')}
              className="w-44"
              inputClassName="h-8 text-sm"
              testId={`${testId}-search`}
            />
          )}
        </div>
      </div>

      <Separator />

      {/* Row list */}
      <div className="divide-border bg-surface-blue max-h-100 divide-y overflow-y-scroll rounded-sm px-3 py-2.5">
        {filteredItems.length === 0 && (
          <p
            data-testid={`${testId}-empty`}
            className="text-muted-foreground py-8 text-center text-sm"
          >
            {items.length === 0
              ? t('attributeComparisonList.empty')
              : t('attributeComparisonList.noSearchResults')}
          </p>
        )}
        {filteredItems.map((item, i) => {
          const onRowClick = getRowClickHandler?.(item);
          return (
            <ComparisonRow
              key={i}
              testId={`${testId}-row-${toTestIdSlug(item.title)}`}
              title={item.title}
              beforeValue={item.beforeValue}
              afterValue={item.afterValue}
              maxValue={maxValue}
              formatValue={formatValue}
              showBefore={showBefore}
              showAfter={showAfter}
              {...(item.badge ? { badge: item.badge } : {})}
              {...(onRowClick ? { onRowClick } : {})}
            />
          );
        })}
      </div>
    </div>
  );
}
